import { useMutation, useQuery } from '@tanstack/react-query';
import { Avatar, Button, Form, Input, Modal, Pagination, Tooltip } from 'antd';
import { FormInstance } from 'antd/lib';
import dayjs from 'dayjs';
import moment from 'moment-timezone';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { fileExportApi, shipToBuyerApi, shippoApi, storageItemApi, storageProcessApi } from '../../../apis';
import {
  CreateShipmentDto,
  ParcelsDtoDistanceUnitEnum,
  ParcelsDtoMassUnitEnum,
  Rate,
  RequestManagementResponse,
  UpdatePreparingDoneDto,
} from '../../../apis/client-axios';
import { SvgAvatarDefault } from '../../../components/@svg/SvgAvatarDefault';
import SvgBlueCircle from '../../../components/@svg/SvgBlueCircle';
import SvgBoxDrop from '../../../components/@svg/SvgBoxDrop';
import SvgCoppy from '../../../components/@svg/SvgCheck';
import SvgCloseModalIcon from '../../../components/@svg/SvgCloseModalIcon';
import { SvgPrintIcon } from '../../../components/@svg/SvgPrintIcon';
import SvgIncrease from '../../../components/@svg/SvgScanIcon';
import { CustomCommonButton } from '../../../components/buttons/common/CustomButton';
import DatepickerCustom from '../../../components/DatepickerCustom';
import FormWrap from '../../../components/FormWrap';
import CustomSearchInput from '../../../components/input/CustomSearchInput';
import NotificationError from '../../../components/notifications/NotificationError';
import NotificationSuccess from '../../../components/notifications/NotificationSuccess';
import { RootState } from '../../../store';
import { QUERY_KEY, STATUS_ITEM_MANAGEMENT, statusChat, TIME_FORMAT } from '../../../util/constants';
import {
  formatDate,
  handleShowInternalDiscussStatus,
  Helper,
  renderStatus,
  renderStatusImage,
} from '../../../util/helper';
import { decode as decodeHtml } from 'he';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SvgMatch } from '../../../components/@svg/SvgMatch';
import { SvgNotMatch } from '../../../components/@svg/SvgNotMatch';
import CheckboxCustom from '../../../components/buttonCustom/CheckboxCustom';
import ButtonDetail from '../../../components/buttons/ButtonDetail';
import TableWrap from '../../../components/TableWrap';
import { ADMIN_ROUTE_PATH } from '../../../constants/router';
import CustomCheckbox from '../../../components/checkbox';
import { AmericaTimezone } from '../../../util/function-util';
import SvgArrowDown from '../../../components/@svg/SvgArrowDown';
import SvgFilterSort from '../../../components/@svg/SvgFilterSort';
import SvgCarrierTooltipInfo from './components/SvgCarrierTooltipInfo';
import SimpleTooltip from './components/SimpleTooltip';
import saveAs from 'file-saver';
import DownloadButtonWithArrow from '../../../components/buttons/DowloadButtonWithArrrow';
import { InchDisplay } from '../itemMangement/itemDetail/components/InchDisplay';
import { cmToInchParts, inchPartsToCmRoundUp, InchParts, Quarter } from '../itemMangement/itemDetail/helper';
import './index.scss';

interface IItemDetail {
  sku: string;
  internalSku: string;
  productName: string;
  shipToBuyerItemId: number;
  labelUrl: string | undefined;
  battery: string;
  shippingCarrier: string;
  storageLength?: number;
  storageWidth?: number;
  storageHeight?: number;
  storageWeight?: number;
}

interface ICheckSku {
  empty: boolean;
  isMatch: boolean;
}

interface IMultiShippingItem {
  rowKey: string;
  shipToBuyerItemId: number;
  shipToBuyerRequestId: number;
  internalSku: string;
  sku: string;
  productName: string;
  battery: string;
  itemPrice?: number;
  inputValue: string;
  isChecked: boolean;
  isMatch: boolean | null;
}

interface IMultiShippingDimension {
  length?: number;
  width?: number;
  height?: number;
  weight?: number;
}

interface IMultiShippingCarrierSelection {
  shippingCarrier: string;
  shippingPlan: string;
  shippingRate: string;
}

interface ITabAction {
  activeTab: number;
  fullTextSearch: string | undefined;
  checkboxValue: boolean;
  formattedStartDate: string | undefined;
  formattedEndDate: string | undefined;
  tab:
    | 'STOCK'
    | 'SHIP'
    | 'PHOTO_INIT'
    | 'PHOTO_REQUEST'
    | 'DISPOSAL'
    | 'QUESTION'
    | 'CHAT'
    | 'ALL'
    | 'WAITING_STOCK'
    | 'IN_STOCK'
    | 'ITEM_SHIP'
    | 'ITEM_DISPOSAL'
    | 'COMPLETE';
  sort: string;
}

type TColumnSort = 'ebayReturnsEbayReturnDeadline';
type TTypeSort = 'ASC' | 'DESC';

type TFilterSort = { columns: TColumnSort; sort: TTypeSort };

const n = (key: keyof UpdatePreparingDoneDto) => key;

const formatCmDimensionValue = (value?: number) => {
  if (value == null || !Number.isFinite(value)) {
    return '';
  }

  return Number(value.toFixed(2)).toString();
};

const getStorageRackCode = (item?: any) => item?.storageItemRacks?.[0]?.rack?.code || '';

type RequestManagementQuarterInchInputProps = {
  valueCm?: number;
  onChangeCm?: (cm: number) => void;
  disabled?: boolean;
  className?: string;
  rounding?: 'nearest' | 'floor' | 'ceil';
};

const RequestManagementQuarterInchInput: React.FC<RequestManagementQuarterInchInputProps> = ({
  valueCm,
  onChangeCm,
  disabled = false,
  className,
  rounding = 'nearest',
}) => {
  const [wholeStr, setWholeStr] = useState('');
  const [quarter, setQuarter] = useState<Quarter>(0);
  const [fractionFocused, setFractionFocused] = useState(false);
  const [wholeFocused, setWholeFocused] = useState(false);

  const isFocusedRef = useRef(false);
  const wholeInputRef = useRef<HTMLInputElement | null>(null);
  const fractionInputRef = useRef<HTMLInputElement | null>(null);

  const focusEnd = (el: HTMLInputElement | null) => {
    if (!el) return;
    const len = el.value.length;
    requestAnimationFrame(() => {
      el.setSelectionRange(len, len);
    });
  };

  const emit = (wholeText: string, q: Quarter) => {
    const wholeNum = wholeText ? Number(wholeText) : 0;
    const parts: InchParts = {
      whole: wholeNum,
      quarter: q,
    };
    onChangeCm?.(inchPartsToCmRoundUp(parts));
  };

  useEffect(() => {
    if (isFocusedRef.current) return;

    if (valueCm == null || !Number.isFinite(valueCm)) {
      setWholeStr('');
      setQuarter(0);
      return;
    }

    const parts = cmToInchParts(valueCm, rounding);
    setWholeStr(parts.whole > 0 ? String(parts.whole) : '');
    setQuarter(parts.quarter);
  }, [valueCm, rounding]);

  useEffect(() => {
    if (!wholeInputRef.current) return;
    const el = wholeInputRef.current;
    requestAnimationFrame(() => {
      el.scrollLeft = el.scrollWidth;
    });
  }, [wholeStr]);

  const stepQuarter = (dir: 1 | -1) => {
    const currentWholeNum = wholeStr ? Number(wholeStr) : 0;
    let totalQuarters = currentWholeNum * 4 + quarter + dir;

    if (totalQuarters < 0) {
      totalQuarters = 0;
    }

    const newWholeNum = Math.floor(totalQuarters / 4);
    const newQuarter = (totalQuarters % 4) as Quarter;
    const newWholeStr = newWholeNum > 0 ? String(newWholeNum) : '';

    setWholeStr(newWholeStr);
    setQuarter(newQuarter);
    emit(newWholeStr, newQuarter);
  };

  const handleWholeChange = (value: string) => {
    const digitsOnly = value.replace(/\D/g, '');

    if (!digitsOnly) {
      setWholeStr('');
      emit('', quarter);
      return;
    }

    const normalized = digitsOnly.replace(/^0+(?=\d)/, '');
    setWholeStr(normalized);
    emit(normalized, quarter);
  };

  return (
    <div className={`request-management__inch-input ${className ?? ''} ${disabled ? 'is-disabled' : ''}`}>
      <div className="request-management__inch-input-inner">
        <input
          ref={wholeInputRef}
          className={`request-management__inch-input-whole ${
            wholeFocused || fractionFocused ? 'request-management__inch-input-whole--active' : ''
          }`}
          disabled={disabled}
          type="text"
          inputMode="numeric"
          value={wholeStr}
          onFocus={(e) => {
            isFocusedRef.current = true;
            setWholeFocused(true);
            focusEnd(e.currentTarget);
          }}
          onBlur={() => {
            isFocusedRef.current = false;
            setWholeFocused(false);
          }}
          onChange={(e) => {
            handleWholeChange(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Tab' && !e.shiftKey) {
              e.preventDefault();
              fractionInputRef.current?.focus();
            }
          }}
        />

        <div className="request-management__inch-input-fraction">
          <span className="request-management__inch-input-pipe" />

          <div
            className={`request-management__inch-input-fraction-box ${
              fractionFocused ? 'request-management__inch-input-fraction-box--active' : ''
            }`}
          >
            <input
              ref={fractionInputRef}
              className="request-management__inch-input-fraction-input"
              disabled={disabled}
              readOnly
              onFocus={(e) => {
                isFocusedRef.current = true;
                setFractionFocused(true);
                focusEnd(e.currentTarget);
              }}
              onBlur={() => {
                isFocusedRef.current = false;
                setFractionFocused(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Tab' && e.shiftKey) {
                  e.preventDefault();
                  wholeInputRef.current?.focus();
                  return;
                }

                if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  stepQuarter(1);
                  return;
                }

                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  stepQuarter(-1);
                  return;
                }

                if (e.key === 'Tab') {
                  return;
                }

                e.preventDefault();
              }}
            />

            <div className="request-management__inch-input-fraction-display">
              <span className="request-management__inch-input-fraction-part">{quarter}</span>
              <span className="request-management__inch-input-fraction-part">/</span>
              <span className="request-management__inch-input-fraction-part">4</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const RequestManagement: React.FC = () => {
  const { authUser } = useSelector((state: RootState) => state.auth);
  const [form] = Form.useForm<FormInstance>();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [lstData, setLstData] = useState<RequestManagementResponse[]>([]);
  const [internalSkuInput, setInternalSkuInput] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [showExportDropdown, setShowExportDropdown] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isMultiOpen, setIsMultiOpen] = useState<boolean>(false);
  const [enableApi, setEnableApi] = useState<boolean>(false);
  const [isCheckSku, setIsCheckSku] = useState<ICheckSku>({
    empty: true,
    isMatch: false,
  });
  const [checkPin, setCheckPin] = useState(false);
  const [isClickBtnPrint, setIsClickBtnPrint] = useState(false);
  const [multiShippingItems, setMultiShippingItems] = useState<IMultiShippingItem[]>([]);
  const multiShippingItemsRef = useRef<IMultiShippingItem[]>([]);
  useEffect(() => {
    multiShippingItemsRef.current = multiShippingItems;
  }, [multiShippingItems]);
  const [multiShippingStep, setMultiShippingStep] = useState<'scan' | 'dimension' | 'carrier' | 'confirm'>('scan');
  const [multiShippingDimension, setMultiShippingDimension] = useState<IMultiShippingDimension>({});
  const [multiShippingWeightInput, setMultiShippingWeightInput] = useState('');
  const [multiShippingDimensionError, setMultiShippingDimensionError] = useState({
    length: false,
    width: false,
    height: false,
    weight: false,
  });
  const [multiShippingDetailData, setMultiShippingDetailData] = useState<any>(null);
  const [multiCarrierOptions, setMultiCarrierOptions] = useState<Rate[]>([]);
  const [multiSelectedCarrier, setMultiSelectedCarrier] = useState<IMultiShippingCarrierSelection | null>(null);
  const [multiCheckPin, setMultiCheckPin] = useState(false);
  const [checkingMultiRowKey, setCheckingMultiRowKey] = useState<string | null>(null);
  const [isFetchingMultiCarrier, setIsFetchingMultiCarrier] = useState(false);
  const [isSubmittingMultiShipping, setIsSubmittingMultiShipping] = useState(false);
  const [isMultiSuccessOpen, setIsMultiSuccessOpen] = useState(false);
  const [multiSuccessLabelUrl, setMultiSuccessLabelUrl] = useState<string | null>(null);
  const [isMultiSuccessLabelPrinted, setIsMultiSuccessLabelPrinted] = useState(false);
  const [filter, setFilter] = useState<TFilterSort>({ columns: null, sort: null });

  const navigate = useNavigate();
  const inputRef = useRef<any>(null);
  const multiInputRefs = useRef<Record<string, any>>({});
  const lastFocusedMultiOpen = useRef(false);
  const multiCheckTimeouts = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const [detailItem, setDetailItem] = useState<IItemDetail>({
    sku: '',
    internalSku: '',
    productName: '',
    shipToBuyerItemId: 0,
    labelUrl: '',
    battery: '',
    shippingCarrier: '',
  });
  const intl = useIntl();
  const role = authUser?.role || '';
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchText, setSearchText] = useState<string>('');
  const refDebounceSearch = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    data: countRequest,
    refetch: refechCount,
    isFetching: isFetchingCount,
  } = useQuery({
    queryKey: [QUERY_KEY.COUNT_TAB_REQUEST, searchParams.get('tab')],
    staleTime: 0,
    queryFn: () => storageItemApi.storageItemControllerCountForRequestList(),
  });

  const exportExcel = useMutation({
    mutationKey: ['exportExcelRequest'],
    mutationFn: (type: string) => fileExportApi.fileExportControllerExportCsvRequest(type, { responseType: 'blob' }),
    onSuccess: ({ data }, type) => {
      const fileName = dayjs().format(TIME_FORMAT.MESSAGE_TIME);
      saveAs(
        data as any,
        type === STATUS_ITEM_MANAGEMENT.SHIPPING_PREPARING
          ? `$新規発送依頼_${fileName.replace(' ', '_')}.xlsx`
          : `$発送準備完了_${fileName.replace(' ', '_')}.xlsx`,
      );
    },
  });

  const handleDownloadNewShip = () => exportExcel.mutate(STATUS_ITEM_MANAGEMENT.SHIPPING_PREPARING);
  const handleDownloadPrepareDone = () => exportExcel.mutate(STATUS_ITEM_MANAGEMENT.SHIPPING_PREPARING_DONE);

  const tabTitles = useMemo(
    () => [
      {
        index: 0,
        name: intl.formatMessage({ id: 'request.inspection' }),
        text_scheduling: intl.formatMessage({ id: 'request.arrival_date' }),
        quantity: countRequest?.data?.stock,
        role: ['operation'],
        tab: 'STOCK',
      },
      {
        index: 1,
        name: intl.formatMessage({ id: 'request.warehousing_processing' }),
        text_scheduling: intl.formatMessage({ id: 'request.arrival_date' }),
        quantity: countRequest?.data?.stock,
        role: ['administrator'],
        tab: 'STOCK',
      },
      {
        index: 2,
        name: intl.formatMessage({ id: 'request.TAKE_BY_OPERATION' }),
        text_scheduling: intl.formatMessage({ id: 'request.date_photo_shot_rq' }),
        quantity: countRequest?.data?.photoInit,
        role: ['administrator', 'operation'],
        tab: 'PHOTO_INIT',
      },
      {
        index: 3,
        name: intl.formatMessage({ id: 'request.shipping' }),
        text_scheduling: intl.formatMessage({ id: 'request.request_ship' }),
        text_checkBox: intl.formatMessage({ id: 'request.new_shipping_requests' }),
        quantity: countRequest?.data?.ship,
        role: ['administrator', 'operation'],
        tab: 'SHIP',
      },
      {
        index: 4,
        name: intl.formatMessage({ id: 'request.additional_photo_shoot' }),
        text_scheduling: intl.formatMessage({ id: 'request.date_photo_shot_rq' }),
        text_checkBox: intl.formatMessage({ id: 'request.show_resolved' }),
        quantity: countRequest?.data?.photoRequest,
        role: ['administrator', 'operation'],
        tab: 'PHOTO_REQUEST',
      },
      {
        index: 5,
        name: intl.formatMessage({ id: 'request.image_video_upload' }),
        text_scheduling: intl.formatMessage({ id: 'request.arrival_date' }),
        quantity: countRequest?.data?.photoInit,
        role: ['staff'],
        tab: 'PHOTO_INIT',
      },
      {
        index: 6,
        name: intl.formatMessage({ id: 'request.upload_additional_photos' }),
        text_scheduling: intl.formatMessage({ id: 'request.date_photo_shot_rq' }),
        text_checkBox: intl.formatMessage({ id: 'request.show_resolved' }),
        quantity: countRequest?.data?.photoRequest,
        role: ['staff'],
        tab: 'PHOTO_REQUEST',
      },
      {
        index: 7,
        name: intl.formatMessage({ id: 'request.discard' }),
        text_scheduling: intl.formatMessage({ id: 'request.disposal_request_date' }),
        text_checkBox:
          role === 'staff'
            ? intl.formatMessage({ id: 'request.show_resolved' })
            : intl.formatMessage({ id: 'request.show_completed' }),
        quantity: countRequest?.data?.disposal,
        role: ['staff', 'administrator', 'operation'],
        tab: 'DISPOSAL',
      },
      {
        index: 8,
        name: intl.formatMessage({ id: 'request.inquiry' }),
        text_scheduling: intl.formatMessage({ id: 'request.mesage_sent_date' }),
        text_checkBox: intl.formatMessage({ id: 'request.show_completed_responses' }),
        quantity: countRequest?.data?.question,
        role: ['administrator', 'staff'],
        tab: 'QUESTION',
      },
      {
        index: 9,
        name: intl.formatMessage({ id: 'request.internal_message' }),
        text_scheduling: intl.formatMessage({ id: 'request.mesage_sent_date' }),
        text_checkBox: intl.formatMessage({ id: 'request.show_completed_responses' }),
        quantity: countRequest?.data?.chat,
        role: ['administrator', 'operation', 'staff'],
        tab: 'CHAT',
      },
    ],
    [countRequest?.data],
  );

  const filteredTabTitles = useMemo(
    () => tabTitles?.filter((tab) => tab.role.some((r) => r === role)),
    [role, tabTitles],
  );
  const [tabAction, setTabAction] = useState<ITabAction>({
    activeTab: 0,
    checkboxValue: false,
    fullTextSearch: undefined,
    formattedStartDate: undefined,
    formattedEndDate: undefined,
    tab: searchParams.get('tab') ? searchParams.get('tab') : (tabTitles[0].tab as any),
    sort: undefined,
  });
  const {
    data: storageItem,
    refetch: refetchStorageItem,
    isFetching: isFetchingStorageItem,
  } = useQuery({
    queryKey: [
      QUERY_KEY.STORAGE_ITEM,
      tabAction.activeTab,
      tabAction.checkboxValue,
      tabAction.fullTextSearch,
      tabAction.formattedStartDate,
      tabAction.formattedEndDate,
      tabAction.tab,
      pageSize,
      currentPage,
      filter.sort,
    ],
    enabled: !!authUser?.id,
    placeholderData: undefined,
    staleTime: 0,
    queryFn: async () => {
      if (role === 'administrator') {
        return await storageItemApi.storageItemControllerStorageManagementAdmin(
          currentPage,
          pageSize,
          filter.columns && filter.sort ? `${filter.columns}-${filter.sort}` : null,
          tabAction.fullTextSearch,
          undefined,
          tabAction.formattedStartDate,
          tabAction.formattedEndDate,
          undefined,
          searchParams.get('tab') ? searchParams.get('tab') : (tabAction.tab as any),
          tabAction.checkboxValue,
        );
      } else if (role === 'staff') {
        return await storageItemApi.storageItemControllerStorageManagementStaff(
          currentPage,
          pageSize,
          undefined,
          tabAction.fullTextSearch,
          undefined,
          tabAction.formattedStartDate,
          tabAction.formattedEndDate,
          undefined,
          searchParams.get('tab') ? searchParams.get('tab') : ('PHOTO_INIT' as any),
          tabAction.checkboxValue,
        );
      } else if (role === 'operation') {
        return await storageItemApi.storageItemControllerStorageManagementOperation(
          currentPage,
          pageSize,
          filter.columns && filter.sort ? `${filter.columns}-${filter.sort}` : null,
          tabAction.fullTextSearch,
          undefined,
          tabAction.formattedStartDate,
          tabAction.formattedEndDate,
          undefined,
          searchParams.get('tab') ? searchParams.get('tab') : (tabAction.tab as any),
          tabAction.checkboxValue,
        );
      }
      return null;
    },
  });

  const handleTabChange = (index) => {
    if (filteredTabTitles[tabAction.activeTab]?.index === index) return;
    setLstData([]);
    setCurrentPage(1);
    setSearchText('');
    refechCount();
    setTabAction({
      fullTextSearch: undefined,
      formattedStartDate: undefined,
      formattedEndDate: undefined,
      tab: searchParams.get('tab')
        ? searchParams.get('tab')
        : (filteredTabTitles.find((t) => t.index == index)?.tab as any),
      activeTab: filteredTabTitles.findIndex((t) => t.index == index),
      checkboxValue: false,
      sort: filter.sort,
    });
    setSearchParams({ ['tab']: filteredTabTitles.find((t) => t.index == index)?.tab });
  };

  const handlePageChange = (page, pageSize) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const handleShowSizeChange = (current, size) => {
    setCurrentPage(current);
    setPageSize(size);
  };

  const getTypeSort = (col: TColumnSort): TTypeSort | null =>
    filter.columns !== col ? 'DESC' : filter.sort === 'DESC' ? 'ASC' : null;

  const handleFilterSort = (col: TColumnSort, order: TTypeSort | null) => {
    setFilter({
      columns: order ? col : null,
      sort: order ?? null,
    });
    setCurrentPage(1);
  };

  const handleCopy = (text, textSuccess, textFailure) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        NotificationSuccess({ content: intl.formatMessage({ id: textSuccess }) });
      })
      .catch((err) => {
        NotificationError({ content: intl.formatMessage({ id: textFailure }) });
      });
  };
  const resetState = () => {
    setInternalSkuInput('');
    setIsCheckSku({
      empty: true,
      isMatch: false,
    });
    setIsClickBtnPrint(false);
    form.resetFields();
  };

  const resetMultiShippingState = () => {
    setMultiShippingStep('scan');
    setMultiShippingDimension({});
    setMultiShippingWeightInput('');
    setMultiShippingDimensionError({
      length: false,
      width: false,
      height: false,
      weight: false,
    });
    setMultiShippingDetailData(null);
    setMultiCarrierOptions([]);
    setMultiSelectedCarrier(null);
    setMultiCheckPin(false);
    setCheckingMultiRowKey(null);
    setIsFetchingMultiCarrier(false);
    setIsSubmittingMultiShipping(false);
  };

  const closeMultiShippingSuccessModal = () => {
    setIsMultiSuccessOpen(false);
    setMultiSuccessLabelUrl(null);
    setIsMultiSuccessLabelPrinted(false);
  };

  useEffect(() => {
    if (authUser && authUser.id) {
      setEnableApi(true);
    }
  }, [authUser]);

  useEffect(() => {
    if (searchParams && searchParams.get('tab')) {
      setTabAction({
        ...tabAction,
        tab: searchParams.get('tab') ? searchParams.get('tab') : (filteredTabTitles[0].tab as any),
        activeTab: filteredTabTitles.findIndex((item) => item.tab == searchParams.get('tab')) || 0,
      });
    } else {
      const find = filteredTabTitles.findIndex((item) => item.tab == searchParams.get('tab'));
      setTabAction({
        ...tabAction,
        tab: tabTitles[0].tab as any,
        activeTab: find > 0 ? find : 0,
      });
    }
  }, [searchParams, filteredTabTitles, tabTitles]);

  const activeTabIndex = filteredTabTitles[tabAction.activeTab]?.index;
  const showShippingPreparing = [3].includes(activeTabIndex);
  const showNotification = [8, 9].includes(activeTabIndex);
  const showRemarks = [0, 1, 2, 3, 4, 5, 6, 7].includes(activeTabIndex);
  const showInquiryDate = [8].includes(activeTabIndex);
  const showTrackingNumber = [0, 1, 5].includes(activeTabIndex);
  const showDeadlineRefund = [0, 1].includes(activeTabIndex);
  const shouldMergeShippingByParent = activeTabIndex === 3;
  const getParentRowSpan = (record) => {
    if (record?.isFirstItem) {
      return {
        rowSpan: record?.count,
        className: 'request-management__merged-cell',
        style: { verticalAlign: 'top' as const, paddingTop: '8px' },
      };
    }

    return {
      rowSpan: 0,
      className: 'request-management__merged-cell',
      style: { verticalAlign: 'top' as const, paddingTop: '8px' },
    };
  };

  const columns = [
    showShippingPreparing && {
      title: '',
      key: 'action',
      className: 'col_9',
      width: 60,
      onCell: (record) => {
        if (shouldMergeShippingByParent) {
          return getParentRowSpan(record);
        }
      },
      render: (item) =>
        renderStatus(item) === STATUS_ITEM_MANAGEMENT.SHIPPING_PREPARING ? (
          <Button onClick={() => openShippingModal(item)} className="request-management__popup-shippng">
            <SvgBoxDrop />
          </Button>
        ) : null,
    },
    {
      title: '',
      key: 'detail',
      className: `col_10 ${[8, 9].includes(activeTabIndex) ? 'padding-right-unset' : ''}`,
      width: [8, 9].includes(activeTabIndex) ? 68 : 100,
      render: (item) => (
        <ButtonDetail
          content={intl.formatMessage({ id: 'item.detail' }) + ' '}
          buttonProps={{
            onClick: (e) => {
              if (e.ctrlKey || e.metaKey) {
                window.open(`${ADMIN_ROUTE_PATH.ITEM_MANAGEMENT_DETAIL}/${item?.id}`, '_blank');
              } else {
                navigate(`${ADMIN_ROUTE_PATH.ITEM_MANAGEMENT_DETAIL}/${item?.id}`);
              }
            },
          }}
        />
      ),
    },
    showNotification && {
      title: '',
      key: 'notification',
      className: 'col_11',
      width: 32,
      render: (item) => (
        <div className="width-20 height-20">
          {activeTabIndex === 9 &&
            item?.itemMessageGroup?.itemInternalMessages &&
            !item?.itemMessageGroup?.itemInternalMessages?.internalMessageReadHistories?.id && <SvgBlueCircle />}
          {activeTabIndex === 8 &&
            item?.itemMessageGroup?.itemInquiryMessages &&
            item?.itemMessageGroup?.itemInquiryMessages?.sendUserId &&
            item?.itemMessageGroup?.itemInquiryMessages?.isRead === 0 && <SvgBlueCircle />}
        </div>
      ),
    },
    {
      title: intl.formatMessage({ id: 'request.SKU' }),
      dataIndex: 'internalSku',
      key: 'sku',
      className: 'col_1',
      width: [7, 9].includes(activeTabIndex) ? 180 : 160,
      render: (text, item) => (
        <div className="request-management__col-copy">
          <Button
            className="request-management__btn-copy"
            onClick={() =>
              handleCopy(
                item.internalSku,
                intl.formatMessage({ id: 'common.copy' }),
                intl.formatMessage({ id: 'common.dont_copy' }),
              )
            }
          >
            <SvgCoppy />
          </Button>
          <p>{item.internalSku}</p>
        </div>
      ),
    },
    {
      title: intl.formatMessage({ id: 'request.customer' }),
      dataIndex: 'customer',
      key: 'customer',
      className: 'col_2',
      width: activeTabIndex == 3 ? 140 : activeTabIndex == 9 ? 200 : [7, 8].includes(activeTabIndex) ? 180 : 136,
      onCell: (record) => {
        if ([0, 1, 2, 5].includes(activeTabIndex) || shouldMergeShippingByParent) {
          return getParentRowSpan(record);
        }
      },
      render: (_, item) =>
        [0, 1, 2, 5].includes(activeTabIndex) || shouldMergeShippingByParent ? (
          <div className={`${item.count > 1 ? 'diff diff--no-top' : ''}`}>
            <p className="text-1-line">{item.isFirstItem && item.storageRequest?.user?.name}</p>
            <span>{item.isFirstItem && item.storageRequest?.user?.userCode}</span>
          </div>
        ) : (
          <div>
            <p className="text-1-line">{item.storageRequest?.user?.name}</p>
            <span>{item.storageRequest?.user?.userCode}</span>
          </div>
        ),
    },
    {
      title: intl.formatMessage({ id: 'request.product_name' }),
      dataIndex: 'itemName',
      key: 'productName',
      className: 'col_product-name',
      width:
        activeTabIndex == 3
          ? 280
          : activeTabIndex == 9
            ? 652
            : activeTabIndex == 5
              ? 376
              : activeTabIndex == 7
                ? 430
                : activeTabIndex == 8
                  ? 732
                  : 396,
      render: (text, item) => <p className="text-2-line break-word">{decodeHtml(item.itemName || '')}</p>,
    },
    {
      title: intl.formatMessage({ id: 'request.situation' }),
      dataIndex: 'situation',
      key: 'situation',
      className: `col_4 ${[9].includes(activeTabIndex) ? 'padding-left-unset' : ''} ${[7].includes(activeTabIndex) ? 'disposal-padding' : ''}`,
      width: [3].includes(activeTabIndex)
        ? 120
        : [8, 9].includes(activeTabIndex)
          ? 160
          : [7].includes(activeTabIndex)
            ? 224
            : 128,
      onCell: (record) => {
        if (shouldMergeShippingByParent) {
          return getParentRowSpan(record);
        }
      },
      render: (text, item) => {
        const inquiryStatus: { [key in (typeof statusChat)[number]]: string } = {
          OPEN: intl.formatMessage({ id: 'chat-item.status.open' }),
          CLOSED: intl.formatMessage({ id: 'chat-item.status.closed' }),
          WAITING_CUSTOMER: intl.formatMessage({ id: 'chat-item.status.inProgress' }),
        };
        if (shouldMergeShippingByParent && !item.isFirstItem) {
          return null;
        }

        if ([0, 1, 2, 5, 7].includes(activeTabIndex)) {
          return (
            <p>
              {intl.formatMessage({ id: `request.${renderStatus(item)}` })}
              {item.status === STATUS_ITEM_MANAGEMENT.IN_STORAGE && item.storageItemRacks.length > 0
                ? `_${item.storageItemRacks[0]?.rack?.code}`
                : ''}
            </p>
          );
        } else if (activeTabIndex === 3) {
          return <p> {intl.formatMessage({ id: `request.${renderStatus(item)}` })}</p>;
        } else if (activeTabIndex === 4) {
          return <p>{intl.formatMessage({ id: `request.${renderStatusImage(item.takePhotoRequest?.status)}` })}</p>;
        } else if (activeTabIndex === 8) {
          return <p>{inquiryStatus[item?.inquiryStatus]}</p>;
        } else if (activeTabIndex === 9) {
          return (
            <p>
              {handleShowInternalDiscussStatus({
                status: item?.internalDiscussStatus,
                isRead: item?.itemMessageGroup?.itemInternalMessages?.internalMessageReadHistories?.id ? 1 : 0,
                authUserId: authUser?.id,
                sendBy: item?.itemMessageGroup?.itemInternalMessages?.sendStaffId,
                intlMultiLang: intl,
              })}
            </p>
          );
        } else if (activeTabIndex === 6) {
          return <p>{intl.formatMessage({ id: `request.${renderStatusImage(item.takePhotoRequest?.status)}` })}</p>;
        }
      },
    },
    showTrackingNumber && {
      title: intl.formatMessage({ id: 'request.tracking_number' }),
      key: 'tracking_number',
      className: 'col_14',
      width: 208,
      render: (item) => {
        const tracking = item?.shippoShippingLabel?.trackingNumber ?? item?.storageRequest?.trackingNumberProvided;

        const formatTracking = (str) => {
          if (!str) return '';
          const part1 = str.slice(0, 10);
          const part2 = str.slice(10, 14);
          const part3 = str.slice(14);
          return `${part1} ${part2} ${part3}`.trim();
        };

        return <p>{formatTracking(tracking)}</p>;
      },
    },
    showShippingPreparing && {
      title: intl.formatMessage({ id: 'request.rackno' }),
      key: 'rack',
      className: 'col_12',
      width: 136,
      onCell: (record) => {
        if (shouldMergeShippingByParent && record?.shouldMergeRackCode) {
          return getParentRowSpan(record);
        }
      },
      render: (item) => {
        if (shouldMergeShippingByParent && item?.shouldMergeRackCode && !item?.isFirstItem) {
          return null;
        }

        return <p>{item?.rackCode || getStorageRackCode(item)}</p>;
      },
    },
    showShippingPreparing && {
      title: intl.formatMessage({ id: 'request.carrier' }),
      key: 'carrier',
      className: 'col_13',
      width: 100,
      onCell: (record) => {
        if (shouldMergeShippingByParent) {
          return getParentRowSpan(record);
        }
      },
      render: (item) => (
        <p>
          {shouldMergeShippingByParent
            ? item.isFirstItem && item?.shipToBuyerItem?.shipToBuyerRequest?.shippingCarrier
            : item?.shipToBuyerItem?.shipToBuyerRequest?.shippingCarrier}
        </p>
      ),
    },
    [9].includes(activeTabIndex) && {
      title: intl.formatMessage({ id: 'request.manager' }),
      key: 'manager',
      className: `col_5 ${[9].includes(activeTabIndex) ? 'padding-left-unset' : ''}`,
      width: 160,
      render: (item) => (
        <Avatar
          src={
            item?.itemMessageGroup?.assign?.avatar?.preview ? (
              Helper.getSourceFile(item?.itemMessageGroup?.assign?.avatar?.preview)
            ) : (
              <SvgAvatarDefault />
            )
          }
        />
      ),
    },
    [9].includes(activeTabIndex) && {
      title: intl.formatMessage({ id: 'request.date_send_new' }),
      key: 'dateSendNew',
      className: `col_6 ${[9].includes(activeTabIndex) ? '' : ''}`,
      width: 168,
      render: (item) => <p>{formatDate(item?.itemMessageGroup?.itemInternalMessages?.createdAt)}</p>,
    },
    [0, 1, 2, 5].includes(activeTabIndex) && {
      title: intl.formatMessage({ id: 'request.arrival_date' }),
      key: 'arrivalDate',
      className: 'col_7_1',
      width: 132,
      onCell: (_, index) => {
        if ([0, 1, 2, 5].includes(activeTabIndex)) {
          if (_.isFirstItem) {
            return { rowSpan: _.count };
          } else {
            return { rowSpan: 0 };
          }
        }
      },
      render: (item) =>
        [0, 1, 2, 5].includes(activeTabIndex) ? (
          <div className={`${item.count > 1 ? 'diff' : ''}`}>
            <p>{formatDate(item.isFirstItem && item.storageRequest?.deliveredAt)}</p>
          </div>
        ) : (
          <p>{formatDate(item.storageRequest?.deliveredAt)}</p>
        ),
    },

    [4, 6].includes(activeTabIndex) && {
      title: intl.formatMessage({ id: 'request.date_photo_shot_rq' }),
      key: 'datePhotoShot',
      className: 'col_7_2',
      width: 160,
      render: (item) => <p>{formatDate(item.takePhotoRequest?.createdAt)}</p>,
    },

    showShippingPreparing && {
      title: intl.formatMessage({ id: 'request.request_ship' }),
      key: 'requestShipDate',
      className: 'col_7_3',
      width: activeTabIndex != 3 ? 160 : 112,
      onCell: (record) => {
        if (shouldMergeShippingByParent) {
          return getParentRowSpan(record);
        }
      },
      render: (item) => (
        <p>
          {shouldMergeShippingByParent
            ? item.isFirstItem && formatDate(item.shipToBuyerItem?.shipToBuyerRequest?.createdAt)
            : formatDate(item.shipToBuyerItem?.shipToBuyerRequest?.createdAt)}
        </p>
      ),
    },

    showShippingPreparing && {
      title: intl.formatMessage({ id: 'request.deadline' }),
      key: 'deadline',
      className: 'col_3',
      width: 172,
      onCell: (record) => {
        if (shouldMergeShippingByParent) {
          return getParentRowSpan(record);
        }
      },
      render: (text, item) => {
        if (shouldMergeShippingByParent && !item.isFirstItem) {
          return null;
        }

        return (
          <p className="text-2-line break-word">
            {item?.shipToBuyerItem?.shipToBuyerRequest?.shipByDate
              ? moment(item?.shipToBuyerItem?.shipToBuyerRequest?.shipByDate)
                  .tz(AmericaTimezone)
                  .format(`YYYY/MM/DD HH:mm z`)
              : ''}
          </p>
        );
      },
    },
    [7].includes(activeTabIndex) && {
      title: intl.formatMessage({ id: 'request.disposal_request_date' }),
      key: 'disposalRequestDate',
      className: `col_7_4 ${[7].includes(activeTabIndex) ? 'disposal-padding' : ''}`,
      width: [7].includes(activeTabIndex) ? 120 : 160,
      render: (item) => <p>{formatDate(item.disposalRequest?.createdAt)}</p>,
    },

    showInquiryDate && {
      title: intl.formatMessage({ id: 'request.date_send_new' }),
      key: 'dateSendNew',
      className: 'col_7_5',
      width: 160,
      render: (item) => <p>{formatDate(item.itemMessageGroup?.itemInquiryMessages?.createdAt)}</p>,
    },
    showDeadlineRefund && {
      title: () => {
        const columns: TColumnSort = 'ebayReturnsEbayReturnDeadline';
        return (
          <div className="item-management__table-sort">
            <div>{intl.formatMessage({ id: 'request.deadline_refund' })}</div>
            <div className="" onClick={() => handleFilterSort(columns, getTypeSort(columns))}>
              {filter.columns === columns ? (
                <div className={`${filter.sort === 'DESC' ? 'rotate-reverse' : ''}`}>
                  <SvgArrowDown />
                </div>
              ) : (
                <div>
                  <SvgFilterSort />
                </div>
              )}
            </div>
          </div>
        );
      },
      width: 128,
      key: 'ebayReturnsEbayReturnDeadline',
      className: 'col_15',
      render: (item) => {
        return (
          <>
            <span>
              {item?.ebayReturns?.ebayReturnDeadline
                ? moment(item?.ebayReturns?.ebayReturnDeadline).tz(AmericaTimezone).format(`YYYY/MM/DD HH:mm z`)
                : ''}
            </span>
          </>
        );
      },
    },
    showRemarks && {
      title: intl.formatMessage({ id: 'request.remarks' }),
      key: 'remarks',
      className: 'col_8',
      width: activeTabIndex == 3 ? 260 : [7].includes(activeTabIndex) ? 402 : 380,
      render: (item) => {
        const content = item?.storageItemNote?.content ?? '';
        if (!content) return null;

        return (
          <SimpleTooltip content={content} className={activeTabIndex === 3 ? 'width-diff' : ''}>
            <p className="text-2-line break-word text-pre-line cursor-pointer">{content}</p>
          </SimpleTooltip>
        );
      },
    },
  ].filter(Boolean);

  let content = storageItem?.data?.content ?? [];

  const resultLstData = (content ?? []).flatMap((item, index) => {
    const rackCodes = (item?.storageItems ?? []).map(getStorageRackCode);
    const firstRackCode = rackCodes[0];
    const shouldMergeRackCode =
      !!firstRackCode && rackCodes.length > 1 && rackCodes.every((code) => code === firstRackCode);

    return (item?.storageItems ?? []).map((storageItem, storageIndex) => ({
      ...storageItem,
      isFirstItem: storageIndex === 0,
      count: item?.storageItems?.length ?? 0,
      parentId: index,
      rackCode: rackCodes[storageIndex],
      shouldMergeRackCode,
      rowKey: storageItem?.id ? String(storageItem.id) : `${index}-${storageIndex}`,
    }));
  });

  const buildSingleShippingDetail = (item) => ({
    sku: item.itemSku,
    internalSku: item.internalSku,
    productName: decodeHtml(item.itemName || ''),
    shipToBuyerItemId: item?.shipToBuyerItem?.id || 0,
    labelUrl: item?.shipToBuyerItem?.shipToBuyerRequest?.shippoShippingLabel?.labelUrl ?? undefined,
    battery: item?.shipToBuyerItem?.shipToBuyerRequest?.battery || '',
    shippingCarrier: item?.shipToBuyerItem?.shipToBuyerRequest?.shippingCarrier || '',
    storageLength: item.storageLength,
    storageWidth: item.storageWidth,
    storageHeight: item.storageHeight,
    storageWeight: item.storageWeight,
  });

  const buildMultiShippingItems = (item, detailData?: any) => {
    const detailItems =
      detailData?.storageItems || detailData?.items || detailData?.shipToBuyerItems || detailData?.data?.storageItems;

    if (Array.isArray(detailItems) && detailItems.length) {
      return detailItems.map((row, index) => ({
        rowKey: row?.id ? String(row.id) : `${item.parentId}-${index}`,
        shipToBuyerItemId: row?.shipToBuyerItem?.id || row?.shipToBuyerItemId || 0,
        shipToBuyerRequestId:
          row?.shipToBuyerItem?.shipToBuyerRequestId ||
          row?.shipToBuyerRequestId ||
          item?.shipToBuyerItem?.shipToBuyerRequestId,
        internalSku: row?.internalSku || row?.storageItemInternalSku || '',
        sku: row?.itemSku || row?.sku || '',
        productName: decodeHtml(row?.itemName || row?.productName || ''),
        battery:
          row?.shipToBuyerItem?.shipToBuyerRequest?.battery ||
          detailData?.battery ||
          item?.shipToBuyerItem?.shipToBuyerRequest?.battery,
        itemPrice: Number(row?.itemPrice || 0),
        inputValue: '',
        isChecked: false,
        isMatch: null,
      }));
    }

    return resultLstData
      .filter((row) => row.parentId === item.parentId)
      .map((row) => ({
        rowKey: row.rowKey,
        shipToBuyerItemId: row?.shipToBuyerItem?.id || 0,
        shipToBuyerRequestId: row?.shipToBuyerItem?.shipToBuyerRequestId || 0,
        internalSku: row.internalSku,
        sku: row.itemSku,
        productName: decodeHtml(row.itemName || ''),
        battery: row?.shipToBuyerItem?.shipToBuyerRequest?.battery || '',
        itemPrice: Number(row.itemPrice || 0),
        inputValue: '',
        isChecked: false,
        isMatch: null,
      }))
      .filter(Boolean);
  };

  const getInitialMultiShippingDimension = (_item, _detailData?: any) => {
    return {
      length: undefined,
      width: undefined,
      height: undefined,
      weight: undefined,
    };
  };

  const openShippingModal = async (item) => {
    // Popup nhập 発送サイズ・重量 + chọn carrier (tạo label ở bước này) dùng cho MỌI request thuộc luồng
    // hoãn label, không riêng gói gộp: item chưa lưu kho tạo 発送依頼 1 món cũng chưa có label.
    // OR thêm `isDeferredLabel` (KHÔNG thay điều kiện cũ) nên request cũ vẫn mở đúng popup như trước;
    // thiếu vế này thì request 1 món hoãn label rơi vào popup single — nơi không có chỗ nhập size.
    const isDeferredLabel = Boolean(item?.shipToBuyerItem?.shipToBuyerRequest?.isDeferredLabel);
    if (shouldMergeShippingByParent && (item.count > 1 || isDeferredLabel)) {
      let detailData: any = null;

      try {
        const { data } = await shipToBuyerApi.shipToBuyerControllerGetShipmentForShipMultipleItem(
          item?.shipToBuyerItem?.shipToBuyerRequestId,
        );
        detailData = data;
      } catch (error) {}

      resetMultiShippingState();
      setMultiShippingDetailData(detailData);
      setMultiShippingItems(buildMultiShippingItems(item, detailData));
      setMultiShippingDimension(getInitialMultiShippingDimension(item, detailData));
      setIsMultiOpen(true);
      return;
    }

    setIsOpen(true);
    setDetailItem(buildSingleShippingDetail(item));
    resetState();
  };

  const renderTableContent = () => {
    return (
      <div className="request-management_table">
        <div className="request-management_table_1_wrap">
          <TableWrap
            className=""
            columns={columns}
            data={resultLstData}
            rowKey="rowKey"
            isScroll
            scrollValue={{ y: '53vh' }}
            rowClassName={(_, index) => (_.parentId % 2 === 0 ? 'even-row' : 'odd-row')}
            rowHoverable={false}
            isLoading={isFetchingCount || isFetchingStorageItem}
          />
        </div>
        <div className="request-management__table-pagination-container">
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={storageItem?.data?.total}
            showSizeChanger
            pageSizeOptions={[20, 50, 100]}
            locale={{ items_per_page: intl.formatMessage({ id: 'item.lines' }) }}
            onChange={handlePageChange}
            onShowSizeChange={handleShowSizeChange}
          />
        </div>
      </div>
    );
  };
  const handleCheckboxChange = (e) => {
    setLstData([]);
    setTabAction({
      ...tabAction,
      checkboxValue: e,
    });
    setCurrentPage(1);
  };

  const onFinish = (values: UpdatePreparingDoneDto) => {
    if (values) {
      processPreparingDone.mutate({ shipToBuyerItemId: detailItem.shipToBuyerItemId, internalSku: values.internalSku });
    }
  };

  const onFinishFailed = (values) => {
    NotificationError({
      content: intl.formatMessage({ id: 'request.bar_code_error' }),
    });
  };

  const processPreparingDone = useMutation({
    mutationFn: (payload: UpdatePreparingDoneDto) =>
      storageProcessApi.storageProcessControllerSetShipToBuyPreparingDone(payload),
    onSuccess: () => {
      NotificationSuccess({ content: intl.formatMessage({ id: 'common.updateSuccess' }) });
      refetchStorageItem();
      setIsCheckSku({
        empty: true,
        isMatch: false,
      });
      setIsOpen(false);
    },
    onError: (error: any) => {},
  });

  const checkProcessPreparingDone = useMutation({
    mutationFn: (payload: UpdatePreparingDoneDto) =>
      storageProcessApi.storageProcessControllerCheckSetPreparingDone(payload),
    onSuccess: (res: any) => {
      if (res.data.result) {
        setIsCheckSku({
          empty: false,
          isMatch: true,
        });
      } else {
        setIsCheckSku({
          empty: false,
          isMatch: false,
        });
      }
    },
    onError: (error: any) => {},
  });

  const getMultiCarrierMutation = useMutation({
    mutationKey: ['multiShippingCarrier'],
    mutationFn: (payload: CreateShipmentDto) => shippoApi.shippoApiControllerCreateShipment(payload),
    onSuccess: ({ data }) => {
      setIsFetchingMultiCarrier(false);
      let options = getCarrierOptionsByProvider(Object.values(data?.rates || {}).filter(Boolean) as Rate[]);

      if (!options.length) {
        NotificationError({ content: intl.formatMessage({ id: 'common.fail' }) });
        return;
      }

      const request = getMultiShippingRequestData() as any;
      const previousCarrier = String(request?.previousCarrier || '').trim();

      if (previousCarrier) {
        const selectedProvider = normalizeProviderName(previousCarrier);
        options = options.filter((rate) => normalizeProviderName(rate.provider) === selectedProvider);
      }

      setMultiCarrierOptions(options);
      setMultiSelectedCarrier(getDefaultMultiCarrierSelection(options));
      setMultiShippingStep('carrier');
    },
    onError: () => {
      setIsFetchingMultiCarrier(false);
      NotificationError({ content: intl.formatMessage({ id: 'common.fail' }) });
    },
  });

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      checkProcessPreparingDone.mutate({
        shipToBuyerItemId: detailItem.shipToBuyerItemId,
        internalSku: internalSkuInput,
      });
    }
  };

  const handleMultiInputChange = (rowKey: string, value: string) => {
    setMultiShippingItems((prev) =>
      prev.map((item) =>
        item.rowKey === rowKey
          ? {
              ...item,
              inputValue: value,
              isChecked: value.trim() ? item.isChecked : false,
              isMatch: value.trim() ? item.isMatch : null,
            }
          : item,
      ),
    );

    if (multiCheckTimeouts.current[rowKey]) {
      clearTimeout(multiCheckTimeouts.current[rowKey]);
    }

    if (value.trim()) {
      multiCheckTimeouts.current[rowKey] = setTimeout(() => {
        handleCheckMultiShippingItem(rowKey, value);
      }, 500);
    }
  };

  const getFirstMultiShippingRow = () => {
    const firstRowKey = multiShippingItems[0]?.rowKey;
    return resultLstData.find((row) => row.rowKey === firstRowKey);
  };

  const getMultiShippingRequestData = () =>
    multiShippingDetailData || getFirstMultiShippingRow()?.shipToBuyerItem?.shipToBuyerRequest;

  const getServiceLevelName = (rate: Rate) => {
    const serviceLevel = rate?.servicelevel as { displayName?: string; name?: string } | undefined;
    return serviceLevel?.displayName ?? serviceLevel?.name ?? '';
  };

  const getCarrierOptionsByProvider = (rates: Rate[]) => {
    const allowedProviders = ['UPS', 'USPS', 'FedEx'];
    const grouped = rates.reduce(
      (acc, rate) => {
        if (!rate?.provider || !allowedProviders.includes(rate.provider)) {
          return acc;
        }

        if (!acc[rate.provider]) {
          acc[rate.provider] = rate;
        }

        return acc;
      },
      {} as Record<string, Rate>,
    );

    return allowedProviders.map((provider) => grouped[provider]).filter(Boolean);
  };

  const normalizeProviderName = (value?: string) => {
    const normalized = String(value || '')
      .trim()
      .toUpperCase();

    if (normalized === 'FEDEX') return 'FedEx';
    if (normalized === 'USPS') return 'USPS';
    if (normalized === 'UPS') return 'UPS';

    return value || '';
  };

  // USPSはリチウムイオン電池向けの「H」マーク付き専用ラベルを発行するため、UNラベルの貼付は不要
  const isUspsCarrier = (value?: string) => normalizeProviderName(value) === 'USPS';

  const getDefaultMultiCarrierSelection = (rates: Rate[]) => {
    const request = getMultiShippingRequestData() as any;

    if (!rates.length) {
      return null;
    }

    const previousCarrier = String(request?.previousCarrier || '').trim();

    if (!previousCarrier) {
      const cheapestRate = rates.reduce((prev, curr) => (Number(prev.amount) < Number(curr.amount) ? prev : curr));
      return {
        shippingCarrier: cheapestRate.provider,
        shippingPlan: getServiceLevelName(cheapestRate),
        shippingRate: cheapestRate.objectId,
      };
    }

    const selectedProvider = normalizeProviderName(previousCarrier);
    const selectedPlan = String(request?.shippingPlan || '')
      .trim()
      .toLowerCase();

    const matchedRateByProviderAndPlan =
      rates.find((rate) => {
        const providerMatched = normalizeProviderName(rate.provider) === selectedProvider;
        const serviceName = getServiceLevelName(rate).trim().toLowerCase();
        return providerMatched && selectedPlan && serviceName === selectedPlan;
      }) || rates.find((rate) => normalizeProviderName(rate.provider) === selectedProvider);

    const currentRate = matchedRateByProviderAndPlan ?? rates[0];

    return {
      shippingCarrier: currentRate.provider,
      shippingPlan: getServiceLevelName(currentRate),
      shippingRate: currentRate.objectId,
    };
  };

  const parseAddressLine = (value?: string) => {
    if (!value) {
      return { street1: '', city: '', state: '' };
    }

    const parts = value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

    if (parts.length >= 3) {
      return {
        street1: parts[0],
        city: parts[1],
        state: parts[2],
      };
    }

    return {
      street1: value,
      city: parts[1] ?? parts[0] ?? '',
      state: parts[2] ?? '',
    };
  };

  const getRequestShippingLocation = (request: any) => {
    const parsedAddress = parseAddressLine(request?.shippingAddress);

    return {
      street1:
        request?.shippingStreet1 ||
        request?.street1 ||
        request?.orderShippingAddress ||
        request?.ebayOrderShippingAddress ||
        parsedAddress.street1 ||
        request?.shippingAddress ||
        '',
      street2:
        request?.shippingStreet2 ||
        request?.street2 ||
        request?.orderShippingAddress2 ||
        request?.ebayOrderShippingAddress2 ||
        request?.shippingAddress2 ||
        undefined,
      city:
        request?.shippingCity ||
        request?.city ||
        request?.orderShippingCity ||
        request?.ebayOrderShippingCity ||
        parsedAddress.city ||
        request?.shippingAddress2 ||
        request?.orderBuyerCountry ||
        'N/A',
      state:
        request?.shippingState ||
        request?.state ||
        request?.stateOrProvince ||
        request?.orderShippingStateOrProvince ||
        request?.ebayOrderShippingStateOrProvince ||
        parsedAddress.state ||
        'N/A',
    };
  };

  const extractMultiShippingLabelUrl = (data: any) => {
    return (
      data?.labelUrl ||
      data?.shippoShippingLabel?.labelUrl ||
      data?.shipToBuyerRequest?.shippoShippingLabel?.labelUrl ||
      data?.data?.labelUrl ||
      data?.data?.shippoShippingLabel?.labelUrl ||
      data?.data?.shipToBuyerRequest?.shippoShippingLabel?.labelUrl ||
      null
    );
  };

  const getMultiShippingDetailItemCount = () => {
    const detailItems =
      multiShippingDetailData?.storageItems ||
      multiShippingDetailData?.items ||
      multiShippingDetailData?.shipToBuyerItems ||
      multiShippingDetailData?.data?.storageItems ||
      multiShippingDetailData?.data?.items ||
      multiShippingDetailData?.data?.shipToBuyerItems;

    if (Array.isArray(detailItems) && detailItems.length > 0) {
      return detailItems.length;
    }

    return Math.max(multiShippingItems.length, 1);
  };

  // Chia đều cân nặng kiện hàng cho từng customs item.
  // LUÔN làm tròn XUỐNG (floor) để tổng net_weight của tất cả item không bao
  // giờ vượt quá cân nặng kiện hàng. Nếu dùng toFixed (làm tròn gần nhất),
  // ví dụ 3.6 / 19 = 0.18947 sẽ bị làm tròn LÊN 0.19, rồi 0.19 * 19 = 3.61 > 3.6
  // và Shippo trả lỗi "Combined weight ... cannot be larger than the Parcel Weight".
  const distributeNetWeight = (totalWeight: number, itemCount: number): string => {
    const count = Math.max(itemCount || 0, 1);
    const perItem = Math.floor(((totalWeight || 0) / count) * 100) / 100;
    return perItem.toFixed(2);
  };

  const buildMultiCarrierPayload = (): CreateShipmentDto | null => {
    const request = getMultiShippingRequestData();
    const detailPayload = (multiShippingDetailData?.payload ||
      multiShippingDetailData?.shipmentPayload ||
      multiShippingDetailData?.data?.payload ||
      multiShippingDetailData?.data?.shipmentPayload) as CreateShipmentDto | undefined;
    const detailItemCount = getMultiShippingDetailItemCount();
    const totalWeight = String(multiShippingDimension.weight || 0);
    const parcelWeight = multiShippingDimension.weight || 0;
    // Số customs item thực tế được gán net_weight ở nhánh detailPayload, để
    // ước số chia khớp đúng với số item được map (tránh lệch tổng).
    const detailCustomsItems = detailPayload?.customs_declaration?.items;
    const detailCustomsItemCount =
      Array.isArray(detailCustomsItems) && detailCustomsItems.length > 0 ? detailCustomsItems.length : detailItemCount;
    const distributedWeight = distributeNetWeight(parcelWeight, detailCustomsItemCount);
    const parcelLength = formatCmDimensionValue(multiShippingDimension.length);
    const parcelWidth = formatCmDimensionValue(multiShippingDimension.width);
    const parcelHeight = formatCmDimensionValue(multiShippingDimension.height);

    if (detailPayload) {
      return {
        ...detailPayload,
        sellerId: getFirstMultiShippingRow()?.storageRequest?.user?.id,
        parcels: Array.isArray(detailPayload.parcels)
          ? detailPayload.parcels.map((parcel) => ({
              ...parcel,
              distance_unit: ParcelsDtoDistanceUnitEnum.Cm,
              height: parcelHeight,
              length: parcelLength,
              weight: totalWeight,
              width: parcelWidth,
            }))
          : [
              {
                distance_unit: ParcelsDtoDistanceUnitEnum.Cm,
                height: parcelHeight,
                length: parcelLength,
                mass_unit: ParcelsDtoMassUnitEnum.Kg,
                weight: totalWeight,
                width: parcelWidth,
                metadata: '',
              },
            ],
        customs_declaration: detailPayload.customs_declaration
          ? {
              ...detailPayload.customs_declaration,
              items: detailPayload.customs_declaration.items?.map((item) => ({
                ...item,
                net_weight: distributedWeight,
              })),
            }
          : detailPayload.customs_declaration,
      };
    }

    const receiverAddress = getRequestShippingLocation(request);
    const firstItemPrice = multiShippingItems[0]?.itemPrice;

    if (!request) {
      return null;
    }

    return {
      ebayAccountId: (request as any)?.ebayAccountId,
      extra: {
        insurance: {
          amount: firstItemPrice ? String(firstItemPrice) : undefined,
          currency: firstItemPrice ? 'USD' : undefined,
        } as any,
      } as any,
      address_to: {
        name: request.shippingName || '',
        street1: receiverAddress.street1 || '',
        street2: receiverAddress.street2,
        city: receiverAddress.city || '',
        state: receiverAddress.state || '',
        zip: request.shippingZipCode || '',
        country: request.orderBuyerCountry || 'US',
        phone: request.shippingTelNo || undefined,
      },
      parcels: [
        {
          distance_unit: ParcelsDtoDistanceUnitEnum.Cm,
          height: parcelHeight,
          length: parcelLength,
          mass_unit: ParcelsDtoMassUnitEnum.Kg,
          weight: String(multiShippingDimension.weight),
          width: parcelWidth,
          metadata: '',
        },
      ],
      customs_declaration: {
        eel_pfc: 'NOEEI_30_37_a' as any,
        items: multiShippingItems.map((item) => ({
          quantity: 1,
          description: item.productName,
          mass_unit: 'kg',
          origin_country: 'US',
          hs_code: '',
          sku_code: item.sku,
          net_weight: distributeNetWeight(parcelWeight, multiShippingItems.length),
          value_amount: String(item.itemPrice || 0),
          value_currency: 'USD',
        })),
      },
    };
  };

  const handleCheckMultiShippingItem = async (rowKey: string, directValue?: string) => {
    if (multiCheckTimeouts.current[rowKey]) {
      clearTimeout(multiCheckTimeouts.current[rowKey]);
      delete multiCheckTimeouts.current[rowKey];
    }
    const currentItem = multiShippingItemsRef.current.find((item) => item.rowKey === rowKey);
    const barcode = directValue?.trim() || currentItem?.inputValue?.trim() || '';

    if (!currentItem || !barcode) {
      return;
    }

    try {
      setCheckingMultiRowKey(rowKey);
      const res: any = await storageProcessApi.storageProcessControllerCheckSetPreparingDone({
        shipToBuyerItemId: currentItem.shipToBuyerItemId,
        internalSku: barcode,
      });

      setMultiShippingItems((prev) =>
        prev.map((item) =>
          item.rowKey === rowKey
            ? {
                ...item,
                isChecked: true,
                isMatch: !!res?.data?.result,
              }
            : item,
        ),
      );

      if (res?.data?.result) {
        const currentIndex = multiShippingItemsRef.current.findIndex((item) => item.rowKey === rowKey);
        if (currentIndex !== -1 && currentIndex < multiShippingItemsRef.current.length - 1) {
          const nextItem = multiShippingItemsRef.current[currentIndex + 1];
          setTimeout(() => {
            multiInputRefs.current[nextItem.rowKey]?.focus();
          }, 100);
        }
      }
    } catch (error) {
      NotificationError({
        content: intl.formatMessage({ id: 'request.bar_code_error' }),
      });
    } finally {
      setCheckingMultiRowKey(null);
    }
  };

  const handleNextFromMultiScan = () => {
    if (!isMultiAllMatched) {
      return;
    }

    setMultiShippingStep('dimension');
  };

  const handleChangeMultiDimension = (key: keyof IMultiShippingDimension, value?: number) => {
    setMultiShippingDimension((prev) => ({
      ...prev,
      [key]: value,
    }));
    setMultiShippingDimensionError((prev) => ({
      ...prev,
      [key]: false,
    }));
  };

  const handleNextFromMultiDimension = () => {
    const nextError = {
      length: !multiShippingDimension.length || multiShippingDimension.length <= 0,
      width: !multiShippingDimension.width || multiShippingDimension.width <= 0,
      height: !multiShippingDimension.height || multiShippingDimension.height <= 0,
      weight: !multiShippingDimension.weight || multiShippingDimension.weight <= 0,
    };

    setMultiShippingDimensionError(nextError);

    if (Object.values(nextError).some(Boolean)) {
      NotificationError({ content: intl.formatMessage({ id: 'common.fail' }) });
      return;
    }

    const payload = buildMultiCarrierPayload();

    if (!payload) {
      return;
    }

    setIsFetchingMultiCarrier(true);
    getMultiCarrierMutation.mutate(payload);
  };

  const closeMultiShippingModal = () => {
    setIsMultiOpen(false);
    setMultiShippingItems([]);
    resetMultiShippingState();
    Object.values(multiCheckTimeouts.current).forEach(clearTimeout);
    multiCheckTimeouts.current = {};
  };

  const isMultiAllMatched =
    multiShippingItems.length > 0 && multiShippingItems.every((item) => item.isChecked && item.isMatch === true);
  const multiShippingBattery = multiShippingItems[0]?.battery;
  const isMultiShippingUsps = isUspsCarrier((getMultiShippingRequestData() as any)?.previousCarrier);
  const selectedMultiCarrierRate = multiCarrierOptions.find(
    (carrier) => carrier.objectId === multiSelectedCarrier?.shippingRate,
  );
  const multiInsuranceFee = Number(selectedMultiCarrierRate?.includedInsurancePrice || 0);
  const multiShippingFee = Number(selectedMultiCarrierRate?.amount || 0);
  const multiTotalFee = multiShippingFee + multiInsuranceFee;
  const multiShipFeePaid = Number(getFirstMultiShippingRow()?.shipToBuyerItem?.shipToBuyerRequest?.shipFee || 0);
  const multiFeeDifference = multiTotalFee - multiShipFeePaid;

  const handleSubmitMultiShipping = async () => {
    if (!isMultiAllMatched || !multiSelectedCarrier) {
      return;
    }

    try {
      setIsSubmittingMultiShipping(true);
      const firstMultiItem = multiShippingItems[0];
      const multiCheckResponse: any = await storageProcessApi.storageProcessControllerCheckSetPreparingDoneMulti({
        shipToBuyerRequestId: firstMultiItem.shipToBuyerRequestId,
        internalSku: firstMultiItem.inputValue?.trim() || firstMultiItem.internalSku,
      });

      const response = await storageProcessApi.storageProcessControllerSetShipToBuyPreparingDoneMulti({
        shipToBuyerRequestId: multiShippingItems[0].shipToBuyerRequestId,
        length: multiShippingDimension.length!,
        width: multiShippingDimension.width!,
        height: multiShippingDimension.height!,
        weight: multiShippingDimension.weight!,
        shippingCarrier: multiSelectedCarrier.shippingCarrier,
        shippingPlan: multiSelectedCarrier.shippingPlan,
        shippingRate: multiSelectedCarrier.shippingRate,
      });

      NotificationSuccess({ content: intl.formatMessage({ id: 'common.updateSuccess' }) });
      const latestLabelUrl =
        extractMultiShippingLabelUrl(multiCheckResponse?.data) ||
        extractMultiShippingLabelUrl((response as any)?.data) ||
        extractMultiShippingLabelUrl(multiShippingDetailData) ||
        getFirstMultiShippingRow()?.shipToBuyerItem?.shipToBuyerRequest?.shippoShippingLabel?.labelUrl ||
        null;

      setMultiSuccessLabelUrl(latestLabelUrl);
      setIsMultiSuccessLabelPrinted(false);
      refetchStorageItem();
      setIsMultiOpen(false);
      resetMultiShippingState();
      setIsMultiSuccessOpen(true);
    } catch (error) {
      NotificationError({ content: intl.formatMessage({ id: 'common.fail' }) });
    } finally {
      setIsSubmittingMultiShipping(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setCheckPin(false);
      setTimeout(() => {
        inputRef.current.focus();
      }, 200);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isMultiOpen && multiShippingStep === 'scan' && multiShippingItems.length > 0) {
      if (!lastFocusedMultiOpen.current) {
        lastFocusedMultiOpen.current = true;
        const firstItem = multiShippingItems[0];
        setTimeout(() => {
          multiInputRefs.current[firstItem.rowKey]?.focus();
        }, 200);
      }
    } else if (!isMultiOpen) {
      lastFocusedMultiOpen.current = false;
    }
  }, [isMultiOpen, multiShippingStep, multiShippingItems]);

  useEffect(() => {
    if (refDebounceSearch.current) clearTimeout(refDebounceSearch.current);

    refDebounceSearch.current = setTimeout(() => {
      const v = searchText.trim();

      setCurrentPage(1);
      setTabAction((prev) => ({
        ...prev,
        fullTextSearch: v || undefined,
      }));
    }, 1000);
    return () => {
      if (refDebounceSearch.current) clearTimeout(refDebounceSearch.current);
    };
  }, [searchText]);

  const disabledDateEnd = (current) => {
    if (!!tabAction.formattedStartDate) {
      return current && current < dayjs(tabAction.formattedStartDate).startOf('day');
    } else {
      return undefined;
    }
  };
  const disabledDateStart = (current) => {
    if (!!tabAction.formattedEndDate) {
      return current && current > dayjs(tabAction.formattedEndDate).startOf('day');
    } else {
      return undefined;
    }
  };

  return (
    <div className="request-management">
      <div className="request-management_container">
        <div className="item-management__header">
          <span className="color-title font-weight-600 font-size-26">
            {intl.formatMessage({ id: 'request.title' })}
          </span>
          <div className="right">
            {tabAction.activeTab === 2 && (
              <div className="item-management__btn-download">
                <DownloadButtonWithArrow onClick={() => setShowExportDropdown(!showExportDropdown)} />
                {showExportDropdown && (
                  <div className="item-management__export-dropdown">
                    <div
                      className="item-management__export-option"
                      onClick={() => {
                        handleDownloadNewShip();
                        setShowExportDropdown(false);
                      }}
                    >
                      新規発送依頼
                    </div>
                    <div
                      className="item-management__export-option"
                      onClick={() => {
                        handleDownloadPrepareDone();
                        setShowExportDropdown(false);
                      }}
                    >
                      発送準備完了
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="request-management_tab-title">
          {filteredTabTitles.map((tab) => {
            return (
              <div
                key={tab?.index}
                className={`request-management_tab-title_item width_${role} ${
                  filteredTabTitles[tabAction.activeTab]?.index === tab?.index ? 'active' : ''
                }`}
                onClick={() => handleTabChange(tab?.index)}
              >
                <p className="request-management_tab-title_item_name request-management_tab-title_item-hover">
                  {tab.name}
                </p>
                <span className="request-management_tab-title_item_quantity">{tab.quantity}</span>
              </div>
            );
          })}
        </div>
        <div className="request-management_filter">
          <div className="request-management_filter_date-wrap_date">
            <p className="request-management_filter_date-wrap_date_text m-r-4">
              {filteredTabTitles[tabAction.activeTab]?.text_scheduling}
            </p>
            <DatepickerCustom
              datePickerProp={{
                onChange: (_, dateString: string) => {
                  setTabAction({
                    ...tabAction,
                    formattedStartDate: dateString,
                  });
                  setCurrentPage(1);
                },
                disabledDate: disabledDateStart,
                value: tabAction.formattedStartDate ? dayjs(tabAction.formattedStartDate) : undefined,
              }}
            />
            <span>~</span>
            <DatepickerCustom
              datePickerProp={{
                onChange: (_, dateString: string) => {
                  setTabAction({
                    ...tabAction,
                    formattedEndDate: dateString,
                  });
                  setCurrentPage(1);
                },
                disabledDate: disabledDateEnd,
                value: tabAction.formattedEndDate ? dayjs(tabAction.formattedEndDate) : undefined,
              }}
            />
          </div>
          {filteredTabTitles[tabAction.activeTab]?.text_checkBox && (
            <div className="request-management_filter_checkbox">
              <CheckboxCustom
                checked={tabAction.checkboxValue}
                onChange={handleCheckboxChange}
                content={filteredTabTitles[tabAction.activeTab].text_checkBox}
              />
            </div>
          )}

          <div>
            <CustomSearchInput
              className="font-weight-500 font-size-16 switch-false request-management__search-input"
              placeholder={intl.formatMessage({ id: 'item.searchPlaceholder' })}
              value={searchText}
              onChange={({ target: { value } }) => setSearchText(value)}
            />
          </div>
        </div>
        <div className="request-management_table">{renderTableContent()}</div>
      </div>
      <Modal
        title={
          multiShippingStep === 'confirm'
            ? '発送情報確認'
            : multiShippingStep === 'carrier'
              ? '配送会社選択'
              : multiShippingStep === 'dimension'
                ? 'サイズ・重量入力'
                : intl.formatMessage({ id: 'requestShipping.title' })
        }
        closable={true}
        maskClosable={false}
        open={isMultiOpen}
        centered={true}
        onCancel={closeMultiShippingModal}
        className={'request-management__modal-shipping request-management__modal-shipping-multi'}
        closeIcon={<SvgCloseModalIcon />}
        footer={
          <div className="request-management__btn-container">
            <CustomCommonButton
              isCancelButton={true}
              buttonProps={{
                className: 'request-management__btn-print request-management__btn-print--neutral',
                onClick: () => {
                  if (multiShippingStep === 'dimension') {
                    setMultiShippingStep('scan');
                    return;
                  }

                  if (multiShippingStep === 'confirm') {
                    setMultiShippingStep('carrier');
                    return;
                  }

                  if (multiShippingStep === 'carrier') {
                    setMultiShippingStep('dimension');
                    return;
                  }

                  closeMultiShippingModal();
                },
              }}
              content={
                ['confirm', 'carrier', 'dimension'].includes(multiShippingStep)
                  ? '戻る'
                  : intl.formatMessage({ id: 'common.cancel' })
              }
            />
            <CustomCommonButton
              isCancelButton={true}
              buttonProps={{
                disabled:
                  multiShippingStep === 'scan'
                    ? !isMultiAllMatched || (multiShippingBattery && !isMultiShippingUsps && !multiCheckPin)
                    : multiShippingStep === 'dimension'
                      ? isFetchingMultiCarrier ||
                        !multiShippingDimension.length ||
                        multiShippingDimension.length <= 0 ||
                        !multiShippingDimension.width ||
                        multiShippingDimension.width <= 0 ||
                        !multiShippingDimension.height ||
                        multiShippingDimension.height <= 0 ||
                        !multiShippingDimension.weight ||
                        multiShippingDimension.weight <= 0
                      : multiShippingStep === 'carrier'
                        ? !multiSelectedCarrier
                        : multiShippingStep === 'confirm'
                          ? !multiSelectedCarrier || isSubmittingMultiShipping
                          : false,
                loading:
                  multiShippingStep === 'dimension'
                    ? isFetchingMultiCarrier
                    : multiShippingStep === 'confirm'
                      ? isSubmittingMultiShipping
                      : false,
                className: 'request-management__btn-cancel',
                type: 'primary',
                onClick:
                  multiShippingStep === 'scan'
                    ? handleNextFromMultiScan
                    : multiShippingStep === 'dimension'
                      ? handleNextFromMultiDimension
                      : multiShippingStep === 'carrier'
                        ? () => setMultiShippingStep('confirm')
                        : handleSubmitMultiShipping,
              }}
              content={
                multiShippingStep === 'scan'
                  ? '次へ進む'
                  : multiShippingStep === 'dimension'
                    ? '次へ進む'
                    : multiShippingStep === 'carrier'
                      ? multiSelectedCarrier
                        ? '確認'
                        : '次へ進む'
                      : '確定'
              }
            />
          </div>
        }
      >
        {multiShippingStep === 'scan' && (
          <div className="request-management__multi-scroll-shell">
            <h3 className="request-management__multi-shipping-count">商品数：{multiShippingItems.length}</h3>
            <div className="request-management__multi-shipping-list">
              {multiShippingItems.map((item) => (
                <div className="request-management__multi-shipping-item" key={item.rowKey}>
                  <div className="request-management__product-info">
                    <p>{intl.formatMessage({ id: 'requestShipping.productInfo' })}</p>
                  </div>
                  <div className="request-management__gird-container request-management__gird-container--multi">
                    <div className="request-management__row-container">
                      <div className="request-management__col-left-container">
                        <span>BayPack管理番号</span>
                      </div>
                      <div className="request-management__col-right-container">
                        <span>{item.internalSku}</span>
                      </div>
                    </div>
                    <div className="request-management__row-container">
                      <div className="request-management__col-left-container">
                        <span>{intl.formatMessage({ id: 'requestShipping.customerSKU' })}</span>
                      </div>
                      <div className="request-management__col-right-container">
                        <span>{item.sku}</span>
                      </div>
                    </div>
                    <div className="request-management__row-container">
                      <div className="request-management__col-left-container">
                        <span>{intl.formatMessage({ id: 'requestShipping.productName' })}</span>
                      </div>
                      <div className="request-management__col-right-container">
                        <p className="text-1-line">{item.productName}</p>
                      </div>
                    </div>
                  </div>

                  <div className="request-management__multi-shipping-form">
                    <div className="request-management__multi-shipping-barcode">
                      <p className="font-size-16">
                        {intl.formatMessage({ id: 'requestShipping.productBarcode' })}
                        <span className="request-management__require">*</span>
                      </p>
                      <Input
                        ref={(el) => {
                          if (el) {
                            multiInputRefs.current[item.rowKey] = el;
                          } else {
                            delete multiInputRefs.current[item.rowKey];
                          }
                        }}
                        suffix={<SvgIncrease />}
                        className="font-size-16"
                        placeholder={intl.formatMessage({ id: 'request.bar_code_placeholder' })}
                        value={item.inputValue}
                        onChange={(e) => handleMultiInputChange(item.rowKey, e.target.value ?? '')}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleCheckMultiShippingItem(item.rowKey, e.currentTarget.value);
                          }
                        }}
                      />
                    </div>

                    <CustomCommonButton
                      isCancelButton={true}
                      buttonProps={{
                        className: 'request-management__btn-confirm font-weight-600',
                        loading: checkingMultiRowKey === item.rowKey,
                        onClick: () => handleCheckMultiShippingItem(item.rowKey, item.inputValue),
                      }}
                      content={intl.formatMessage({ id: 'request.ship_ready.check' })}
                    />
                  </div>

                  {item.isMatch !== null && (
                    <div className="request-management__judgement-container request-management__judgement-container--multi">
                      <div className="request-management__judgement-title">
                        <p>
                          {intl.formatMessage({ id: 'requestShipping.judgement' })}
                          <span>&nbsp;:</span>
                        </p>
                      </div>
                      <div className="request-management__judgement-description">
                        <p className="request-management__status-sku">
                          {item.isMatch ? (
                            <>
                              <span>{intl.formatMessage({ id: 'requestShipping.match' })}</span>
                              <SvgMatch />
                            </>
                          ) : (
                            <>
                              <span>{intl.formatMessage({ id: 'requestShipping.notMatch' })}</span>
                              <SvgNotMatch />
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {multiShippingStep === 'dimension' && (
          <div className="request-management__multi-dimension">
            <div className="request-management__multi-dimension-list">
              <div className="request-management__multi-dimension-item">
                <label>縦</label>
                <RequestManagementQuarterInchInput
                  valueCm={multiShippingDimension.length}
                  onChangeCm={(value) => handleChangeMultiDimension('length', value)}
                  className={multiShippingDimensionError.length ? 'request-management__input-error' : ''}
                  rounding="ceil"
                />
                <span className="unit">inch</span>
              </div>
              <div className="request-management__multi-dimension-item">
                <label>横</label>
                <RequestManagementQuarterInchInput
                  valueCm={multiShippingDimension.width}
                  onChangeCm={(value) => handleChangeMultiDimension('width', value)}
                  className={multiShippingDimensionError.width ? 'request-management__input-error' : ''}
                  rounding="ceil"
                />
                <span className="unit">inch</span>
              </div>
              <div className="request-management__multi-dimension-item">
                <label>高さ</label>
                <RequestManagementQuarterInchInput
                  valueCm={multiShippingDimension.height}
                  onChangeCm={(value) => handleChangeMultiDimension('height', value)}
                  className={multiShippingDimensionError.height ? 'request-management__input-error' : ''}
                  rounding="ceil"
                />
                <span className="unit">inch</span>
              </div>
            </div>
            <div className="request-management__multi-dimension-list request-management__multi-dimension-list--weight">
              <div className="request-management__multi-dimension-item">
                <label>重量</label>
                <Input
                  type="number"
                  className={`request-management__kg-input ${multiShippingDimensionError.weight ? 'request-management__input-error' : ''}`}
                  value={multiShippingWeightInput}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setMultiShippingWeightInput(raw);
                    handleChangeMultiDimension('weight', raw ? Number(raw) : undefined);
                  }}
                />
                <span className="unit">kg</span>
              </div>
            </div>
          </div>
        )}

        {multiShippingStep === 'carrier' && (
          <div className="request-management__multi-carrier">
            {multiCarrierOptions.map((carrier) => {
              const isActive = multiSelectedCarrier?.shippingRate === carrier.objectId;
              return (
                <label
                  key={carrier.objectId}
                  className={`request-management__multi-carrier-item ${isActive ? 'active' : ''}`}
                >
                  <input
                    type="radio"
                    checked={isActive}
                    onChange={() =>
                      setMultiSelectedCarrier({
                        shippingCarrier: carrier.provider,
                        shippingPlan: getServiceLevelName(carrier),
                        shippingRate: carrier.objectId,
                      })
                    }
                  />
                  <div className="request-management__multi-carrier-card">
                    <span className="carrier-radio" />
                    <img src={`/assets/icons/carrier/${carrier.provider.toLowerCase()}.png`} alt={carrier.provider} />
                    <div className="request-management__multi-carrier-content">
                      <p className="request-management__multi-carrier-name">{carrier.provider}</p>
                      <p className="request-management__multi-carrier-price">${carrier.amount}</p>
                      <p className="request-management__multi-carrier-days">
                        お届け目安: {carrier.estimatedDays ? `${carrier.estimatedDays}日` : '-'}
                      </p>
                    </div>
                    <div className="request-management__multi-carrier-info">
                      <SimpleTooltip content={getServiceLevelName(carrier)}>
                        <span className="request-management__multi-carrier-info-icon" aria-hidden="true">
                          <SvgCarrierTooltipInfo />
                        </span>
                      </SimpleTooltip>
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        )}

        {multiShippingStep === 'confirm' && multiSelectedCarrier && selectedMultiCarrierRate && (
          <div className="request-management__multi-confirm">
            <div className="request-management__multi-confirm-section">
              <h4 className="request-management__multi-confirm-title">パッケージ情報</h4>
              <div className="request-management__multi-confirm-package">
                <div className="request-management__multi-confirm-package-row">
                  <div className="request-management__multi-confirm-package-item">
                    <span className="label">縦</span>
                    <span className="colon">:</span>
                    <span className="value">
                      <InchDisplay cm={multiShippingDimension.length} rounding="ceil" />
                    </span>
                  </div>
                  <div className="request-management__multi-confirm-package-item">
                    <span className="label">横</span>
                    <span className="colon">:</span>
                    <span className="value">
                      <InchDisplay cm={multiShippingDimension.width} rounding="ceil" />
                    </span>
                  </div>
                  <div className="request-management__multi-confirm-package-item">
                    <span className="label">高さ</span>
                    <span className="colon">:</span>
                    <span className="value">
                      <InchDisplay cm={multiShippingDimension.height} rounding="ceil" />
                    </span>
                  </div>
                </div>
                <div className="request-management__multi-confirm-package-item request-management__multi-confirm-package-item--weight">
                  <span className="label">重量</span>
                  <span className="colon">:</span>
                  <span className="value">
                    <span className="inch-display-wrap">
                      <span className="inch-display">
                        <span className="inch-display__whole">{multiShippingDimension.weight}</span>
                      </span>
                      <span className="inch-display__unit">kg</span>
                    </span>
                  </span>
                </div>
              </div>
            </div>

            <div className="request-management__multi-confirm-divider" />

            <div className="request-management__multi-confirm-section">
              <h4 className="request-management__multi-confirm-title">配送会社</h4>
              <div className="request-management__multi-confirm-carrier">
                <div className="request-management__multi-carrier-card">
                  <img
                    src={`/assets/icons/carrier/${selectedMultiCarrierRate.provider.toLowerCase()}.png`}
                    alt={selectedMultiCarrierRate.provider}
                  />
                  <div className="request-management__multi-carrier-content">
                    <p className="request-management__multi-carrier-name">{selectedMultiCarrierRate.provider}</p>
                    <p className="request-management__multi-carrier-price">${selectedMultiCarrierRate.amount}</p>
                    <p className="request-management__multi-carrier-days">
                      お届け目安:{' '}
                      {selectedMultiCarrierRate.estimatedDays ? `${selectedMultiCarrierRate.estimatedDays}日` : '-'}
                    </p>
                  </div>
                  <div className="request-management__multi-confirm-info">
                    <SimpleTooltip content={getServiceLevelName(selectedMultiCarrierRate)}>
                      <span className="request-management__multi-carrier-info-icon" aria-hidden="true">
                        <SvgCarrierTooltipInfo />
                      </span>
                    </SimpleTooltip>
                  </div>
                </div>
              </div>
            </div>

            <div className="request-management__multi-confirm-section">
              <h4 className="request-management__multi-confirm-title">オプション</h4>
              <div className="request-management__multi-confirm-option">
                <span>保険料</span>
                <span>${multiInsuranceFee.toFixed(2)}</span>
              </div>
            </div>

            <div className="request-management__multi-confirm-divider" />

            <div className="request-management__multi-confirm-total">
              <h4>合計金額</h4>
              <p>${multiTotalFee.toFixed(2)}</p>
            </div>
            {multiFeeDifference < 0 && (
              <div className="request-management__multi-confirm-total" style={{ marginTop: '12px' }}>
                <h4 style={{ fontSize: '16px', color: '#514b4d', fontWeight: 500 }}>返金</h4>
                <p style={{ fontSize: '16px', color: '#514b4d', fontWeight: 600 }}>
                  ${Math.abs(multiFeeDifference).toFixed(2)}
                </p>
              </div>
            )}
            {multiFeeDifference > 0 && (
              <div className="request-management__multi-confirm-total" style={{ marginTop: '12px' }}>
                <h4 style={{ fontSize: '16px', color: '#514b4d', fontWeight: 500 }}>追加請求</h4>
                <p style={{ fontSize: '16px', color: '#514b4d', fontWeight: 600 }}>${multiFeeDifference.toFixed(2)}</p>
              </div>
            )}
            {multiFeeDifference === 0 && (
              <div className="request-management__multi-confirm-total" style={{ marginTop: '12px' }}>
                <h4 style={{ fontSize: '16px', color: '#514b4d', fontWeight: 500 }}>差額なし</h4>
                <p style={{ fontSize: '16px', color: '#514b4d', fontWeight: 600 }}>$0.00</p>
              </div>
            )}
          </div>
        )}

        {multiShippingStep === 'scan' && multiShippingBattery && !isMultiShippingUsps ? (
          <div className="request-management__pin request-management__pin--multi">
            <CustomCheckbox
              name="pinMulti"
              checked={multiCheckPin}
              onChange={({ target: { checked } }) => setMultiCheckPin(checked)}
            />
            <p>
              以下のUNラベルを貼付しました
              <br />
              {multiShippingBattery.match(/UN\d+/g)}
            </p>
          </div>
        ) : null}
      </Modal>
      <Modal
        closable={true}
        open={isMultiSuccessOpen}
        centered={true}
        onCancel={closeMultiShippingSuccessModal}
        className={
          'request-management__modal-shipping request-management__modal-shipping-multi request-management__modal-shipping-success'
        }
        closeIcon={<SvgCloseModalIcon />}
        footer={
          <div className="request-management__btn-container request-management__btn-container--success">
            <CustomCommonButton
              isCancelButton={true}
              buttonProps={{
                className: 'request-management__btn-print request-management__btn-print--success',
                onClick: () => {
                  if (multiSuccessLabelUrl) {
                    const nextWindow = window.open('', '_blank');
                    if (nextWindow) {
                      nextWindow.location.href = multiSuccessLabelUrl;
                    }
                    setIsMultiSuccessLabelPrinted(true);
                  }
                },
              }}
              content={
                <p>
                  <span className="request-management__multi-success-print-icon" aria-hidden="true">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M18.353 14H19C19.943 14 20.414 14 20.707 13.707C21 13.414 21 12.943 21 12V11C21 9.114 21 8.172 20.414 7.586C19.828 7 18.886 7 17 7H7C5.114 7 4.172 7 3.586 7.586C3 8.172 3 9.114 3 11V13C3 13.471 3 13.707 3.146 13.854C3.293 14 3.53 14 4 14H5.647"
                        stroke="#1C86ED"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M18 7V5.88C18 4.872 18 4.368 17.804 3.983C17.6314 3.64411 17.3559 3.36861 17.017 3.196C16.632 3 16.128 3 15.12 3H8.88C7.872 3 7.368 3 6.983 3.196C6.64411 3.36861 6.36861 3.64411 6.196 3.983C6 4.368 6 4.872 6 5.88V7M6 20.306V12C6 11.057 6 10.586 6.293 10.293C6.586 10 7.057 10 8 10H16C16.943 10 17.414 10 17.707 10.293C18 10.586 18 11.057 18 12V20.306C18 20.623 18 20.781 17.896 20.856C17.792 20.931 17.642 20.881 17.342 20.781L15.158 20.053C15.08 20.027 15.041 20.013 15 20.013C14.96 20.013 14.92 20.027 14.842 20.053L12.158 20.947C12.08 20.973 12.041 20.987 12 20.987C11.96 20.987 11.92 20.973 11.842 20.947L9.158 20.053C9.08 20.027 9.041 20.013 9 20.013C8.96 20.013 8.92 20.027 8.842 20.053L6.658 20.781C6.358 20.881 6.208 20.931 6.104 20.856C6 20.781 6 20.623 6 20.306Z"
                        stroke="#1C86ED"
                        strokeWidth="1.5"
                      />
                      <path d="M10 14H13M10 17H14.5" stroke="#1C86ED" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </span>
                  <span>ラベル印刷</span>
                </p>
              }
            />
            <CustomCommonButton
              isCancelButton={true}
              buttonProps={{
                disabled: !isMultiSuccessLabelPrinted,
                className: 'request-management__btn-cancel',
                type: 'primary',
                onClick: closeMultiShippingSuccessModal,
              }}
              content={'OK'}
            />
          </div>
        }
      >
        <div className="request-management__multi-success">
          <div className="request-management__multi-success-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80" fill="none">
              <circle opacity="0.2" cx="40" cy="40" r="40" fill="#1C86ED" />
              <circle opacity="0.4" cx="40" cy="40" r="32" fill="#1C86ED" />
              <circle cx="40" cy="40" r="24" fill="#1C86ED" />
              <path
                d="M46.6292 32.3343C46.3231 32.164 45.9864 32.0557 45.6385 32.0156C45.2905 31.9755 44.938 32.0043 44.6011 32.1006C44.2643 32.1968 43.9498 32.3585 43.6755 32.5763C43.4012 32.7942 43.1725 33.064 43.0026 33.3703L38.0519 42.2797L35.2186 39.4463C34.9726 39.1916 34.6783 38.9885 34.353 38.8487C34.0276 38.709 33.6777 38.6354 33.3236 38.6323C32.9695 38.6293 32.6184 38.6967 32.2907 38.8308C31.963 38.9649 31.6652 39.1629 31.4148 39.4133C31.1645 39.6637 30.9664 39.9614 30.8324 40.2891C30.6983 40.6169 30.6308 40.968 30.6339 41.3221C30.637 41.6762 30.7105 42.0261 30.8503 42.3514C30.99 42.6768 31.1932 42.971 31.4479 43.217L36.7812 48.5503C37.2852 49.0557 37.9652 49.333 38.6666 49.333L39.0359 49.3063C39.4446 49.2492 39.8345 49.0979 40.1749 48.8644C40.5152 48.631 40.7967 48.3217 40.9972 47.961L47.6639 35.961C47.8341 35.6549 47.9424 35.3184 47.9825 34.9704C48.0226 34.6225 47.9938 34.2701 47.8977 33.9334C47.8016 33.5966 47.6402 33.2821 47.4225 33.0077C47.2049 32.7333 46.9353 32.5045 46.6292 32.3343Z"
                fill="white"
              />
            </svg>
          </div>
          <h3 className="request-management__multi-success-title">配送ラベルが発行されました</h3>
        </div>
      </Modal>
      <Modal
        title={intl.formatMessage({ id: 'requestShipping.title' })}
        closable={true}
        open={isOpen}
        maskClosable={false}
        centered={true}
        onCancel={() => setIsOpen(false)}
        className={'request-management__modal-shipping'}
        closeIcon={<SvgCloseModalIcon />}
        footer={
          <div className="request-management__btn-container">
            <CustomCommonButton
              isCancelButton={true}
              buttonProps={{
                className: 'request-management__btn-print',
                onClick: () => {
                  if (detailItem.labelUrl) {
                    window.open(detailItem.labelUrl, '_blank');
                    setIsClickBtnPrint(true);
                  }
                },
                disabled: !isCheckSku.isMatch || processPreparingDone.isPending || !detailItem.labelUrl,
              }}
              content={
                <p>
                  <SvgPrintIcon />
                  {intl.formatMessage({ id: 'common.print' })}
                </p>
              }
            />
            <CustomCommonButton
              isCancelButton={true}
              buttonProps={{
                disabled:
                  !isCheckSku.isMatch ||
                  processPreparingDone.isPending ||
                  (detailItem.battery && !isUspsCarrier(detailItem.shippingCarrier) && !checkPin) ||
                  !isClickBtnPrint,
                loading: processPreparingDone.isPending,
                className: 'request-management__btn-cancel',
                type: 'primary',
                onClick: () => form.submit(),
              }}
              content={intl.formatMessage({ id: 'common.ready' })}
            />
          </div>
        }
      >
        <div className="request-management__product-info">
          <p>{intl.formatMessage({ id: 'requestShipping.productInfo' })}</p>
        </div>
        <div className="request-management__gird-container">
          <div className="request-management__row-container">
            <div className="request-management__col-left-container">
              <span>BayPack管理番号</span>
            </div>
            <div className="request-management__col-right-container">
              <span>{detailItem.internalSku}</span>
            </div>
          </div>
          <div className="request-management__row-container">
            <div className="request-management__col-left-container">
              <span>{intl.formatMessage({ id: 'requestShipping.customerSKU' })}</span>
            </div>
            <div className="request-management__col-right-container">
              <span>{detailItem.sku}</span>
            </div>
          </div>
          <div className="request-management__row-container">
            <div className="request-management__col-left-container">
              <span>{intl.formatMessage({ id: 'requestShipping.productName' })}</span>
            </div>
            <div className="request-management__col-right-container">
              <p className="text-1-line">{detailItem.productName}</p>
            </div>
          </div>
          <div className="request-management__storage-size-block">
            <h6 className="request-management__storage-size-title">
              {intl.formatMessage({ id: 'item.detail.storageSize' })}
            </h6>
          </div>
          <div className="request-management__row-container">
            <div className="request-management__storage-size-values">
              <span className="request-management__storage-size-item">
                {intl.formatMessage({ id: 'item.detail.length' })}
                <span className="request-management__storage-size-colon">:</span>{' '}
                <InchDisplay cm={detailItem.storageLength ?? 0} rounding="ceil" />
              </span>
              <span className="request-management__storage-size-item">
                {intl.formatMessage({ id: 'item.detail.width' })}
                <span className="request-management__storage-size-colon">:</span>{' '}
                <InchDisplay cm={detailItem.storageWidth ?? 0} rounding="ceil" />
              </span>
              <span className="request-management__storage-size-item">
                {intl.formatMessage({ id: 'item.detail.height' })}
                <span className="request-management__storage-size-colon">:</span>{' '}
                <InchDisplay cm={detailItem.storageHeight ?? 0} rounding="ceil" />
              </span>
              <span className="request-management__storage-size-item">
                {intl.formatMessage({ id: 'item.detail.storageWeight' })}
                <span className="request-management__storage-size-colon">:</span> {detailItem.storageWeight ?? 0} kg
              </span>
            </div>
          </div>
        </div>
        <FormWrap
          form={form}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          className="request-management__form-request-shiping"
        >
          <Form.Item
            name={n('internalSku')}
            className="request-management__product-bar-code m-b-12"
            label={
              <p className="font-size-16">
                {intl.formatMessage({ id: 'requestShipping.productBarcode' })}
                <span className="request-management__require">*</span>
              </p>
            }
            required
            rules={[{ required: true, message: '' }]}
          >
            <Input
              suffix={<SvgIncrease />}
              autoFocus={true}
              className="font-size-16"
              ref={inputRef}
              placeholder={intl.formatMessage({ id: 'request.bar_code_placeholder' })}
              onChange={(e) => {
                const value = e.target.value ?? '';
                setInternalSkuInput(value);
                if (value == '') {
                  resetState();
                }
              }}
              onKeyDown={handleKeyDown}
            />
          </Form.Item>
          <Form.Item className="m-b-12">
            <CustomCommonButton
              isCancelButton={true}
              buttonProps={{
                className: 'request-management__btn-confirm font-weight-600',
                onClick: () => {
                  if (internalSkuInput.trim().length > 0) {
                    checkProcessPreparingDone.mutate({
                      shipToBuyerItemId: detailItem.shipToBuyerItemId,
                      internalSku: internalSkuInput.trim(),
                    });
                  } else {
                    form.validateFields();
                  }
                },
              }}
              content={intl.formatMessage({ id: 'request.ship_ready.check' })}
            />
          </Form.Item>
        </FormWrap>
        <div className="request-management__judgement-container">
          <div className="request-management__judgement-title">
            {isCheckSku.empty ? (
              <></>
            ) : (
              <p>
                {intl.formatMessage({ id: 'requestShipping.judgement' })}
                <span>&nbsp;:</span>
              </p>
            )}
          </div>
          <div className="request-management__judgement-description">
            {isCheckSku.empty ? (
              <></>
            ) : (
              <p className="request-management__status-sku">
                {isCheckSku.isMatch ? (
                  <>
                    <span>{intl.formatMessage({ id: 'requestShipping.match' })}</span>
                    <SvgMatch />
                  </>
                ) : (
                  <>
                    {' '}
                    <span>{intl.formatMessage({ id: 'requestShipping.notMatch' })}</span>
                    <SvgNotMatch />
                  </>
                )}
              </p>
            )}
          </div>
        </div>
        {detailItem.battery && !isUspsCarrier(detailItem.shippingCarrier) ? (
          <div className="request-management__pin">
            <CustomCheckbox
              name="pin"
              checked={checkPin}
              onChange={({ target: { checked } }) => setCheckPin(checked)}
            />
            <p>
              以下のUNラベルを貼付しました
              <br />
              {detailItem.battery.match(/UN\d+/g)}
            </p>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default RequestManagement;
